import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShieldCheck, Wallet, HeartHandshake, Star, ArrowRight, Apple, Play, Monitor, School, Leaf, MessageSquareOff, Lock, CreditCard, ShieldAlert, Baby, Users, GraduationCap, Store, TrendingUp, Sparkles, Mail, CheckCircle2, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

// Kid-shopfront collage imagery (Unsplash)
const HERO_IMG = {
  shopfront: "https://images.unsplash.com/photo-1596461404969-9ae70f2830c1?w=800&h=800&fit=crop&q=80",
  parent: "https://images.unsplash.com/photo-1516627145497-ae6968895b74?w=800&h=800&fit=crop&q=80",
  seller: "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=800&h=800&fit=crop&q=80",
};

export default function Landing() {
  const [comingSoon, setComingSoon] = useState(null); // null | 'ios' | 'android' | 'both'
  const [email, setEmail] = useState("");
  const [wlStatus, setWlStatus] = useState("idle"); // idle | loading | success | error
  const [wlMessage, setWlMessage] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isOpen = comingSoon !== null;
  // Iteration 293 — live impact bar for the Circular Economy section.
  const [impact, setImpact] = useState(null);
  useEffect(() => {
    api.get("/impact/community")
      .then(({ data }) => setImpact(data))
      .catch(() => setImpact(null));
  }, []);
  const storeLabel =
    comingSoon === "ios" ? "App Store" :
    comingSoon === "android" ? "Google Play" :
    "App Stores";

  // Stripe / auth callback catch-all — if Stripe (or any upstream service)
  // ever redirects back to the site root with a callback param instead of
  // /dashboard/parent, bounce the user to their dashboard immediately so
  // the top-up/purchase confirmation logic actually runs. Runs before the
  // waitlist deep-link effect so callback URLs never accidentally open the
  // waitlist modal.
  useEffect(() => {
    const callbackKeys = [
      "topup_session",   // Wallet top-up return (POST /wallet/topup)
      "session_id",      // Generic Stripe Checkout session (buyer checkout)
      "connect",         // Stripe Connect onboarding return
      "kid_connect",     // Stripe Connect kid onboarding return
      "auth_callback",   // Future OAuth callback (Emergent Google Auth)
    ];
    const matched = callbackKeys.find((k) => searchParams.get(k));
    if (!matched) return;
    const dest = matched === "session_id"
      ? `/checkout/success?${searchParams.toString()}`
      : `/dashboard/parent?${searchParams.toString()}`;
    navigate(dest, { replace: true });
  }, [searchParams, navigate]);

  // Deep-link from the footer or dashboard: /?waitlist=1 or /?waitlist=open pops
  // the modal with a "both" preset. Depends on `searchParams` so intra-Landing
  // footer clicks (URL changes but no remount) also re-trigger the modal. The
  // `comingSoon === null` guard prevents a re-open loop after the effect clears
  // the param via replace.
  useEffect(() => {
    const wl = searchParams.get("waitlist");
    if ((wl === "1" || wl === "open") && comingSoon === null) {
      setComingSoon("both");
      searchParams.delete("waitlist");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, comingSoon, setSearchParams]);

  const closeModal = () => {
    setComingSoon(null);
    // reset only after the close animation is done so the UI doesn't flicker
    setTimeout(() => {
      setEmail("");
      setWlStatus("idle");
      setWlMessage("");
    }, 200);
  };

  const submitWaitlist = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setWlStatus("error");
      setWlMessage("Please enter a valid email.");
      return;
    }
    setWlStatus("loading");
    setWlMessage("");
    try {
      await api.post("/waitlist", { email: trimmed, store: comingSoon });
      setWlStatus("success");
      setWlMessage(
        comingSoon === "both"
          ? "We'll email you the moment MyTableTopApp lands on either app store."
          : `We'll email you when ${storeLabel} goes live.`
      );
    } catch (err) {
      setWlStatus("error");
      setWlMessage(formatApiError(err) || "Could not save your email. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Navbar />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-14 lg:pt-20 pb-16 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div
            className="inline-flex items-center gap-2 rounded-full bg-brandPink/15 text-brandPink font-display font-semibold px-5 py-2.5 text-xl sm:text-2xl md:text-3xl whitespace-nowrap"
            data-testid="hero-eyebrow"
          >
            <Store className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 flex-shrink-0" /> Toddlers to Teens Marketplace
          </div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight font-semibold text-brandSlate leading-[1.08] mt-5" data-testid="hero-headline">
            MyTableTopApp is the # 1 platform where kids can safely buy and sell online—with full parental supervision.
          </h1>
          <p className="text-lg text-slate-600 mt-5 max-w-2xl leading-relaxed" data-testid="hero-subheadline">
            A secure, e-commerce marketplace for toddlers to teens to buy and sell while learning real-world business, finance, and sustainability skills — completely managed and approved by parents.
          </p>
          <ul className="mt-5 space-y-3 max-w-2xl" data-testid="hero-feature-bullets">
            <li className="flex items-start gap-2.5 text-slate-700 leading-relaxed" data-testid="hero-bullet-eco">
              <span aria-hidden="true" className="inline-block w-5 flex-shrink-0">🌍</span>
              <span><strong>Save the planet & declutter:</strong> Rehome, reuse, and resell items, redirecting them away from landfills through a child's own digital mini-storefront.</span>
            </li>
            <li className="flex items-start gap-2.5 text-slate-700 leading-relaxed" data-testid="hero-bullet-pta">
              <span aria-hidden="true" className="inline-block w-5 flex-shrink-0">🏫</span>
              <span><strong>Community fundraising:</strong> Generate sales and profits that can be automatically split (e.g., 50/50) with local school PTAs and favorite causes.</span>
            </li>
          </ul>

          {/* Iteration 303 — dev preview viewport toggles removed from the
              hero. Store badges stay: App Store + Google Play (Coming soon)
              with the waitlist capture flow. */}
          <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-cta-row">
            <button
              type="button"
              onClick={() => setComingSoon("ios")}
              className="btn-secondary relative"
              data-testid="cta-app-store"
            >
              <Apple className="w-5 h-5" /> App Store
              <span className="ml-2 pill bg-brandPink/15 text-brandPink text-[10px] !py-0.5 !px-2">Coming soon</span>
            </button>
            <button
              type="button"
              onClick={() => setComingSoon("android")}
              className="btn-secondary relative"
              data-testid="cta-play-store"
            >
              <Play className="w-5 h-5" /> Google Play
              <span className="ml-2 pill bg-brandTeal/15 text-brandTeal text-[10px] !py-0.5 !px-2">Coming soon</span>
            </button>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500" data-testid="hero-badge-row">
            <div className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-brandTeal" /> AI moderated</div>
            <div className="flex items-center gap-1.5"><HeartHandshake className="w-4 h-4 text-brandPink" /> Parent approved</div>
            <div className="flex items-center gap-1.5"><Wallet className="w-4 h-4 text-brandSlate" /> Stripe secure</div>
            <div className="flex items-center gap-1.5" data-testid="hero-badge-pta"><School className="w-4 h-4 text-brandPink" /> PTA Funding Splits</div>
            <div className="flex items-center gap-1.5" data-testid="hero-badge-eco"><Leaf className="w-4 h-4 text-brandTeal" /> Eco-Landfill Tracking</div>
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="absolute -inset-4 bg-gradient-to-br from-brandPink/25 via-white to-brandTeal/25 rounded-[40px] blur-2xl" />
          <div className="relative grid grid-cols-2 gap-4">
            {/* Tile 1: Kid shopfront photo + pink brand overlay */}
            <div
              className="rounded-3xl shadow-pop h-56 p-6 flex flex-col justify-between text-white relative overflow-hidden bg-cover bg-center"
              style={{ backgroundImage: `linear-gradient(160deg, rgba(255,133,161,0.65), rgba(245,106,138,0.9)), url('${HERO_IMG.shopfront}')` }}
              data-testid="hero-tile-shopfront"
            >
              <Store className="w-7 h-7 relative" />
              <div className="relative">
                <div className="text-[11px] font-bold uppercase tracking-[0.15em] opacity-90">Step 01</div>
                <div className="font-display text-xl font-semibold mt-0.5">Kid lists a toy</div>
              </div>
            </div>

            {/* Tile 2: Parent + kid photo + teal brand overlay */}
            <div
              className="rounded-3xl shadow-pop h-40 mt-8 p-6 flex flex-col justify-between text-white relative overflow-hidden bg-cover bg-center"
              style={{ backgroundImage: `linear-gradient(160deg, rgba(32,178,170,0.70), rgba(24,143,137,0.92)), url('${HERO_IMG.parent}')` }}
              data-testid="hero-tile-parent"
            >
              <ShieldCheck className="w-6 h-6 relative" />
              <div className="relative">
                <div className="text-[11px] font-bold uppercase tracking-[0.15em] opacity-90">Step 02</div>
                <div className="font-display text-lg font-semibold leading-tight">Parent approves</div>
              </div>
            </div>

            {/* Tile 3: Stripe checkout — clean slate card, no photo */}
            <div className="rounded-3xl shadow-pop h-40 bg-gradient-to-br from-brandSlate to-brandSlateDark p-6 flex flex-col justify-between text-white relative overflow-hidden" data-testid="hero-tile-stripe">
              <div className="absolute -right-3 -top-3 w-24 h-24 rounded-full bg-white/5" />
              <div className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full bg-white/5" />
              <Wallet className="w-6 h-6 relative" />
              <div className="relative">
                <div className="text-[11px] font-bold uppercase tracking-[0.15em] opacity-80">Step 03</div>
                <div className="font-display text-lg font-semibold leading-tight">Buyer checks out</div>
              </div>
            </div>

            {/* Tile 4: Adult Verified seller — photo header + white card body */}
            <div className="rounded-3xl shadow-pop h-56 bg-white border border-slate-100 -mt-2 overflow-hidden flex flex-col" data-testid="hero-tile-verified">
              <div
                className="h-24 bg-cover bg-center"
                style={{ backgroundImage: `url('${HERO_IMG.seller}')` }}
              />
              <div className="p-4 flex-1 flex flex-col justify-between">
                <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-brandTeal">Adult Verified seller</div>
                <div>
                  <div className="flex items-center gap-1 mb-1">{[0,1,2,3,4].map(i => <Star key={i} className="w-3.5 h-3.5 fill-brandPink text-brandPink" />)}</div>
                  <div className="font-display text-base font-semibold text-brandSlate">Lily, age 9</div>
                  <div className="text-xs text-slate-500">£48 earned · 6 sales</div>
                  <div className="text-[10px] text-brandTeal mt-1 inline-flex items-center gap-1"><HeartHandshake className="w-3 h-3" /> Parent-approved</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LIVE IMPACT METRICS — moved directly under the hero (iteration 315) */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-10" data-testid="impact-metrics-section">
        <div className="card-base p-6 md:p-8 bg-gradient-to-r from-emerald-50 to-brandTeal/10" data-testid="impact-bar">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
            <div data-testid="impact-items-rehomed">
              <div className="text-3xl md:text-4xl font-display font-semibold text-emerald-700">
                {impact ? impact.items_rehomed : "—"}
              </div>
              <div className="text-sm text-slate-500 mt-1">Total Items Rehomed</div>
            </div>
            <div data-testid="impact-weight-diverted">
              <div className="text-3xl md:text-4xl font-display font-semibold text-emerald-700">
                {impact ? `${Number(impact.weight_diverted_kg).toFixed(1)} kg` : "—"}
              </div>
              <div className="text-sm text-slate-500 mt-1">Landfill Waste Diverted</div>
            </div>
            <div data-testid="impact-pta-raised">
              <div className="text-3xl md:text-4xl font-display font-semibold text-emerald-700">
                {impact ? `£${(Number(impact.pta_raised_pennies) / 100).toFixed(2)}` : "—"}
              </div>
              <div className="text-sm text-slate-500 mt-1">PTA Funds Raised</div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — 3 STEPS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16" data-testid="how-it-works-section">
        <div
          className="inline-flex items-center gap-2 rounded-full bg-brandTeal/15 text-brandTeal font-display font-semibold px-5 py-2.5 text-xl sm:text-2xl md:text-3xl whitespace-nowrap"
          data-testid="how-it-works-eyebrow"
        >
          <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 flex-shrink-0" /> How it works
        </div>
        <h2 className="font-display text-3xl md:text-4xl font-medium text-brandSlate mt-3">Earn, safely, in three steps</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-8">
          {[
            {
              i: <Store className="w-8 h-8" />,
              iconWrap: "bg-brandPink/15 text-brandPink",
              n: "01",
              t: "Kid lists an item in their mini-store.",
              d: "Kids snap photos, write a fun description and set a fair price. Our AI helper checks for safety in seconds.",
            },
            {
              i: <ShieldCheck className="w-8 h-8" />,
              iconWrap: "bg-brandTeal/15 text-brandTeal",
              n: "02",
              t: "Parent reviews and approves the listing.",
              d: "The parent gets a tap-to-approve queue. Nothing goes live without you giving the thumbs up.",
            },
            {
              i: <Wallet className="w-8 h-8" />,
              iconWrap: "bg-brandSlate/10 text-brandSlate",
              n: "03",
              t: "Buyer checks out & community wins",
              d: "Buyers pay securely via Stripe. Kids earn pocket money, the app tracks saved landfill weight, and an automatic cut goes directly to your chosen school or PTA.",
            },
          ].map((s, i) => (
            <div key={i} className="card-base p-8 hover:-translate-y-1 hover:shadow-pop transition-all" data-testid={`how-it-works-step-${s.n}`}>
              <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4 ${s.iconWrap}`}>{s.i}</div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-400" data-testid={`how-it-works-step-${s.n}-eyebrow`}>Step {s.n}</div>
              <h3 className="font-display text-xl md:text-2xl font-semibold text-brandSlate mt-1" data-testid={`how-it-works-step-${s.n}-title`}>{s.t}</h3>
              <p className="text-slate-600 mt-3 leading-relaxed" data-testid={`how-it-works-step-${s.n}-body`}>{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SAFETY FIRST — TRUST BADGES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16" data-testid="safety-section">
        <div
          className="inline-flex items-center gap-2 rounded-full bg-brandPink/15 text-brandPink font-display font-semibold px-5 py-2.5 text-xl sm:text-2xl md:text-3xl whitespace-nowrap"
          data-testid="safety-eyebrow"
        >
          <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 flex-shrink-0" /> Safety first
        </div>
        <h2 className="font-display text-3xl md:text-4xl font-medium text-brandSlate mt-3">Trust & Safety By Design</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {[
            { i: <MessageSquareOff className="w-7 h-7" />, wrap: "bg-brandPink/15 text-brandPink", t: "Anti-Bullying", d: "Preset messages only — no free-text typing between children." },
            { i: <Lock className="w-7 h-7" />, wrap: "bg-brandTeal/15 text-brandTeal", t: "PIN Parent Zone", d: "Every sensitive action gated behind the parent's PIN." },
            { i: <CreditCard className="w-7 h-7" />, wrap: "bg-brandSlate/10 text-brandSlate", t: "Isolated Stripe Gateway", d: "Payments never touch a kid's device — Stripe handles it end to end." },
            { i: <ShieldAlert className="w-7 h-7" />, wrap: "bg-brandTeal/15 text-brandTeal", t: "GDPR Privacy Shield", d: "Compliant with UK GDPR & Data Protection: no ads, no data sold." },
          ].map((b, i) => (
            <div key={i} className="card-base p-6" data-testid={`safety-badge-${i}`}>
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-3 ${b.wrap}`}>{b.i}</div>
              <div className="font-display text-lg font-medium text-brandSlate">{b.t}</div>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CIRCULAR ECONOMY — iteration 293/315 */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16" data-testid="circular-economy-section">
        <div
          className="inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-800 font-display font-semibold px-5 py-2.5 text-xl sm:text-2xl md:text-3xl whitespace-nowrap"
          data-testid="circular-badge"
        >
          ♻️ Sustainable Community Impact
        </div>
        <h2 className="font-display text-3xl md:text-4xl font-medium text-brandSlate mt-3" data-testid="circular-headline">
          Turning Clutter Into School Funds & Saved Landfill
        </h2>
        <p className="text-slate-600 mt-2 max-w-2xl" data-testid="circular-subtitle">
          We keep pre-loved goods circulating locally — preventing landfill waste while raising funds for schools and supporting families in need.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {[
            { e: "🤖", t: "AI Auto-Weight Tracking", d: "Calculates kg diverted automatically" },
            { e: "🏫", t: "School & PTA Splits", d: "Automated fundraising splits" },
            { e: "🤝", t: "CSR Family Support Hub", d: "£0.00 PIN-gated gifting for families" },
          ].map((c, i) => (
            <div key={i} className="card-base p-6" data-testid={`circular-pillar-${i}`}>
              <div className="text-3xl mb-3" aria-hidden="true">{c.e}</div>
              <div className="font-display text-lg font-medium text-brandSlate">{c.t}</div>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BUILT TO GROW WITH YOUR FAMILY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16" data-testid="grow-section">
        <div
          className="inline-flex items-center gap-2 rounded-full bg-brandTeal/15 text-brandTeal font-display font-semibold px-5 py-2.5 text-xl sm:text-2xl md:text-3xl whitespace-nowrap"
          data-testid="grow-eyebrow"
        >
          <HeartHandshake className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 flex-shrink-0" /> Built to grow with your family
        </div>
        <h2 className="font-display text-3xl md:text-4xl font-medium text-brandSlate mt-3">A Marketplace That Grows With Them</h2>
        <div className="text-sm font-bold uppercase tracking-wider text-slate-400 mt-1">Ages 4 to 16</div>
        <div className="grid md:grid-cols-3 gap-6 mt-8">
          {[
            { i: <Baby className="w-8 h-8" />, iconWrap: "bg-brandPink/15 text-brandPink", tag: "Early Years", age: "Ages 4–6", t: "Simplified visual dashboard", d: "Chunky buttons, big photos, mostly icons. Kids point at what they want to sell and grown-ups do the rest." },
            { i: <Users className="w-8 h-8" />, iconWrap: "bg-brandTeal/15 text-brandTeal", tag: "Tween Years", age: "Ages 7–13", t: "Guided independence", d: "Kids write listings, pick prices, chat via safe presets. Mandatory parent approval filter before anything goes live." },
            { i: <GraduationCap className="w-8 h-8" />, iconWrap: "bg-brandSlate/10 text-brandSlate", tag: "The Hand-off", age: "Ages 14–16", t: "Digital training wheels for teens", d: "Optional independent banking, direct payouts, ratings, self-serve refunds. Parent still has the emergency override." },
          ].map((s, i) => (
            <div key={i} className="card-base p-8 hover:-translate-y-1 hover:shadow-pop transition-all" data-testid={`grow-card-${i}`}>
              <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4 ${s.iconWrap}`}>{s.i}</div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{s.tag} · {s.age}</div>
              <div className="font-display text-2xl font-medium text-brandSlate mt-1">{s.t}</div>
              <p className="text-slate-600 mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHO WE WORK WITH — iteration 293/315 */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16" data-testid="partners-section">
        <div
          className="inline-flex items-center gap-2 rounded-full bg-brandTeal/15 text-brandTeal font-display font-semibold px-5 py-2.5 text-xl sm:text-2xl md:text-3xl whitespace-nowrap"
          data-testid="partners-badge"
        >
          🤝 Community Ecosystem
        </div>
        <h2 className="font-display text-3xl md:text-4xl font-medium text-brandSlate mt-3" data-testid="partners-headline">
          Uniting Communities for a Sustainable Future
        </h2>
        <p className="text-slate-600 mt-2 max-w-2xl" data-testid="partners-subtitle">
          A single platform connecting everyone who supports our children and planet.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {[
            { e: "👨‍👩‍👧", t: "Parents & Families", d: "Teach financial literacy safely while decluttering the play area." },
            { e: "🏫", t: "Schools & PTAs", d: "Zero-effort, all-year-round continuous digital fundraising—where everyone becomes a winner!" },
            { e: "🎗️", t: "Charities & Support Services", d: "Direct access to £0.00 essentials via PIN access." },
            { e: "🧒", t: "Kids & Teens", d: "Run a real shopfront, earn pocket money, and save the planet." },
            { e: "🏘️", t: "Local Community Groups", d: "Grassroots recycling & sustainability projects." },
            { e: "🏢", t: "Corporate Sponsors & Businesses", d: "Fund purchases to meet CSR & ESG goals." },
          ].map((p, i) => (
            <div key={i} className="card-base p-6" data-testid={`partner-card-${i}`}>
              <div className="text-3xl mb-3" aria-hidden="true">{p.e}</div>
              <div className="font-display text-lg font-medium text-brandSlate">{p.t}</div>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 card-base p-6 md:p-8 text-center bg-gradient-to-r from-brandPink/10 via-white to-brandTeal/10 border-brandPink/20" data-testid="ecosystem-callout-banner">
          <p className="font-display text-xl md:text-2xl font-semibold text-brandSlate">
            Not Vinted. Not eBay. <span className="text-brandTeal">The only kid-safe circular economy built for PTA fundraising.</span>
          </p>
        </div>
      </section>

      {/* OUR STORY — BORN IN HACKNEY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16" data-testid="story-section">
        <div
          className="inline-flex items-center gap-2 rounded-full bg-brandPink/15 text-brandPink font-display font-semibold px-5 py-2.5 text-xl sm:text-2xl md:text-3xl whitespace-nowrap"
          data-testid="hackney-born-accent"
        >
          <School className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 flex-shrink-0" /> Our story
        </div>
        <h2 className="font-display text-3xl md:text-4xl font-medium text-brandSlate mt-3">Born in Hackney, Built for Families</h2>
        <p className="text-slate-600 mt-3 max-w-3xl leading-relaxed">
          MyTableTopApp was created to build a safer, smarter circular economy for families. The platform is independently owned and operated by its founder, who was deeply inspired by her 18-year-old son. To ensure the app was genuinely kid-friendly, early concepts were openly discussed voluntarily around the topic of app dev with some local primary school pupils. Their enthusiastic feedback helped shape an intuitive marketplace designed to empower the next generation.
        </p>
        <div className="grid md:grid-cols-4 gap-4 mt-8">
          {[
            { i: <Wallet className="w-6 h-6 text-brandPink" />, t: "Financial Literacy & Budgeting", c: "bg-brandPink/15 text-brandPink" },
            { i: <TrendingUp className="w-6 h-6 text-brandTeal" />, t: "E-commerce & Marketing", c: "bg-brandTeal/15 text-brandTeal" },
            { i: <HeartHandshake className="w-6 h-6 text-brandPink" />, t: "Customer Service & Negotiation", c: "bg-brandPink/15 text-brandPink" },
            { i: <Leaf className="w-6 h-6 text-brandTeal" />, t: "The Green Initiative", c: "bg-brandTeal/15 text-brandTeal" },
          ].map((p, i) => (
            <div key={i} className="card-base p-6" data-testid={`pillar-${i}`}>
              <div className={`pill ${p.c} mb-3`}>Pillar {i + 1}</div>
              <div className="mb-3">{p.i}</div>
              <div className="font-display text-lg font-medium text-brandSlate">{p.t}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="card-base p-10 md:p-14 bg-gradient-to-br from-white to-brandPink/10">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <div className="flex gap-1 mb-4">
                {[0,1,2,3,4].map(i => <Star key={i} className="w-5 h-5 fill-brandPink text-brandPink" />)}
              </div>
              <p className="font-display text-2xl md:text-3xl text-brandSlate leading-snug">
                &ldquo;My nine-year-old made £48 in a weekend and learned more about money than I did at her age. The parent approval queue gives me total peace of mind.&rdquo;
              </p>
              <div className="mt-5 text-brandTeal font-semibold">— Priya R., MyTableTopApp pilot parent, Hackney</div>
            </div>
            <div className="md:col-span-5">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { v: "100+", l: "families in pilot", c: "text-brandPink" },
                  { v: "£12k+", l: "kid earnings to date", c: "text-brandTeal" },
                  { v: "100%", l: "parent-approved", c: "text-brandSlate" },
                  { v: "< 2s", l: "safety check", c: "text-brandPink" },
                ].map((s, i) => (
                  <div key={i} className="card-base p-5">
                    <div className={`text-3xl font-display font-semibold ${s.c}`}>{s.v}</div>
                    <div className="text-sm text-slate-500 mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-20">
        <div className="card-base p-8 md:p-12 text-center bg-gradient-to-br from-brandPink to-brandTeal text-white border-transparent">
          <h2 className="font-display text-3xl md:text-4xl font-medium">Ready to open your child&rsquo;s first shopfront?</h2>
          <p className="text-white/90 mt-3 max-w-xl mx-auto">One parent account, unlimited kid profiles. Free to join. Fees only when a sale happens.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/auth?mode=register" className="btn-primary !bg-white !text-brandPink hover:!bg-slate-100" data-testid="closing-cta-register">
              Create your parent account <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/browse" className="btn-secondary !bg-brandSlate !text-white hover:!bg-brandSlateDark" data-testid="closing-cta-browse">
              Browse — no login needed
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* COMING SOON MODAL — mobile app store placeholder + waitlist */}
      <Dialog open={isOpen} onOpenChange={(o) => !o && closeModal()}>
        <DialogContent
          className="sm:max-w-md rounded-3xl border-0 p-0 overflow-hidden shadow-pop"
          data-testid="coming-soon-modal"
        >
          {/* Brand banner */}
          <div className="relative bg-gradient-to-br from-brandPink to-brandTeal px-6 pt-8 pb-6 text-white">
            <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-white/10" />
            <div className="absolute -left-4 -bottom-8 w-24 h-24 rounded-full bg-white/10" />
            <div className="relative flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] opacity-90">
              <Sparkles className="w-4 h-4" /> {storeLabel}
            </div>
            <DialogHeader className="relative mt-2 text-left space-y-1">
              <DialogTitle className="font-display text-2xl sm:text-3xl font-semibold text-white">
                Coming Soon to App Stores!
              </DialogTitle>
              <DialogDescription className="text-white/90 text-sm leading-relaxed">
                We&rsquo;re putting the final touches on our iOS and Android apps.
                In the meantime, tap the button below to use <strong>MyTableTopApp</strong> right in your browser.
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Actions */}
          <div className="px-6 py-6 bg-white">
            <a
              href="/app"
              className="btn-primary w-full justify-center"
              data-testid="coming-soon-launch-web"
              onClick={closeModal}
            >
              <Monitor className="w-5 h-5" /> Launch Web App Instead <ArrowRight className="w-4 h-4" />
            </a>

            {/* Waitlist */}
            <div className="mt-6 pt-6 border-t border-slate-100">
              {wlStatus === "success" ? (
                <div
                  className="rounded-2xl bg-brandTeal/10 border border-brandTeal/30 p-4 flex items-start gap-3"
                  data-testid="waitlist-success"
                >
                  <CheckCircle2 className="w-5 h-5 text-brandTeal flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-display text-sm font-semibold text-brandSlate">You&rsquo;re on the list!</div>
                    <div className="text-xs text-slate-600 mt-0.5">{wlMessage}</div>
                  </div>
                </div>
              ) : (
                <form onSubmit={submitWaitlist} data-testid="waitlist-form">
                  <div className="text-xs font-bold uppercase tracking-[0.15em] text-brandTeal mb-2 flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" /> {comingSoon === "both" ? "Get notified the moment we launch" : `Get notified when ${storeLabel} goes live`}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); if (wlStatus === "error") setWlStatus("idle"); }}
                      placeholder="parent@example.com"
                      className="input-base !py-2.5 flex-1"
                      data-testid="waitlist-email-input"
                      disabled={wlStatus === "loading"}
                    />
                    <button
                      type="submit"
                      disabled={wlStatus === "loading"}
                      className="inline-flex items-center justify-center gap-1.5 font-bold px-4 py-2.5 bg-brandTeal text-white rounded-xl hover:bg-brandTealDark transition-all shadow-soft disabled:opacity-60 whitespace-nowrap"
                      data-testid="waitlist-submit-btn"
                    >
                      {wlStatus === "loading" ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                      ) : (
                        <>Notify Me</>
                      )}
                    </button>
                  </div>
                  {wlStatus === "error" && (
                    <div className="text-xs text-rose-600 mt-2" data-testid="waitlist-error">{wlMessage}</div>
                  )}
                  <div className="text-[11px] text-slate-400 mt-2">
                    One quick email when we launch. No spam, no data sold.
                  </div>
                </form>
              )}
            </div>

            <button
              type="button"
              onClick={closeModal}
              className="w-full mt-4 text-sm font-semibold text-slate-500 hover:text-brandSlate transition py-1"
              data-testid="coming-soon-close"
            >
              Maybe later
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
